import './styles.css';
import { createDataService } from './data-service.js';
import {
  config,
  todayISO,
  startOfWeekISO,
  escapeHtml,
  formatDate,
  formatShortDate,
  formatTime,
  formatTimeInput,
  formatDuration,
  minutesBetween,
  getTravelMinutes,
  getServiceMinutes,
  getDelayMinutes,
  routeStatusLabel,
  stopStatusLabel,
  outcomeLabel,
  geolocate,
  mapLink,
  toCSV,
  downloadText
} from './utils.js';

const app = document.querySelector('#app');
const modalRoot = document.querySelector('#modal-root');
const toastRoot = document.querySelector('#toast-root');
const service = createDataService();

const state = {
  loading: true,
  session: null,
  profile: null,
  online: navigator.onLine,
  adminView: 'overview',
  selectedDate: todayISO(),
  routes: [],
  drivers: [],
  subcontractors: [],
  reportFrom: startOfWeekISO(),
  reportTo: todayISO(),
  reportRoutes: [],
  driverRoute: null,
  transfers: [],
  busy: false
};

const icons = {
  route: '↗',
  dashboard: '▦',
  drivers: '♟',
  report: '▤',
  refresh: '↻',
  plus: '+',
  pin: '⌖',
  clock: '◷',
  logout: '⎋',
  edit: '✎',
  copy: '⧉',
  trash: '×',
  check: '✓',
  transfer: '⇄',
  map: '⌁'
};

function setDocumentTitle() {
  document.title = config.appName || 'Suivi Routes';
}

function showToast(title, message = '', type = 'success') {
  const node = document.createElement('div');
  node.className = `toast ${type}`;
  node.innerHTML = `<div><strong>${escapeHtml(title)}</strong>${message ? `<span>${escapeHtml(message)}</span>` : ''}</div>`;
  toastRoot.appendChild(node);
  setTimeout(() => node.remove(), 4300);
}

function openModal(content, className = '') {
  modalRoot.innerHTML = `
    <div class="modal-backdrop" data-action="close-modal-backdrop">
      <section class="modal ${className}" role="dialog" aria-modal="true">
        ${content}
      </section>
    </div>`;
  document.body.style.overflow = 'hidden';
  const focusable = modalRoot.querySelector('input, select, textarea, button');
  setTimeout(() => focusable?.focus(), 30);
}

function closeModal() {
  modalRoot.innerHTML = '';
  document.body.style.overflow = '';
}

function loadingScreen() {
  return `<div class="loading-screen"><div><div class="spinner"></div><p>Chargement de l’application…</p></div></div>`;
}

function statusBadge(status, type = 'route') {
  const label = type === 'stop' ? stopStatusLabel(status) : routeStatusLabel(status);
  return `<span class="badge ${escapeHtml(status)}">${escapeHtml(label)}</span>`;
}

function baseTopbar(subtitle) {
  return `
    <header class="topbar">
      <div class="brand">
        <div class="brand-mark">SR</div>
        <div class="brand-copy">
          <strong>${escapeHtml(config.appName || 'Suivi Routes')}</strong>
          <span>${escapeHtml(subtitle || '')}</span>
        </div>
      </div>
      <div class="topbar-actions">
        ${service.isDemo ? '<span class="badge demo">Mode démo</span>' : ''}
        <span class="online-dot ${state.online ? '' : 'offline'}" aria-hidden="true"></span>
        <span class="connection-label">${state.online ? 'En ligne' : 'Hors ligne'}</span>
        <button class="btn btn-light btn-sm" data-action="logout">${icons.logout} Déconnexion</button>
      </div>
    </header>`;
}

function adminTabs() {
  const tabs = [
    ['overview', icons.dashboard, 'Aujourd’hui'],
    ['routes', icons.route, 'Routes'],
    ['drivers', icons.drivers, 'Comptes'],
    ['reports', icons.report, 'Rapports']
  ];
  return `
    <div class="tabs-wrap">
      <nav class="tabs" aria-label="Navigation administrateur">
        ${tabs.map(([id, icon, label]) => `<button class="tab ${state.adminView === id ? 'active' : ''}" data-action="admin-nav" data-view="${id}">${icon} ${label}</button>`).join('')}
      </nav>
    </div>
    <nav class="mobile-bottom-nav" aria-label="Navigation mobile">
      ${tabs.map(([id, icon, label]) => `<button class="mobile-nav-btn ${state.adminView === id ? 'active' : ''}" data-action="admin-nav" data-view="${id}"><span>${icon}</span>${label}</button>`).join('')}
    </nav>`;
}

function loginView() {
  return `
    <main class="login-page">
      <section class="login-hero">
        <div>
          <div class="login-logo"><div class="brand-mark">SR</div>${escapeHtml(config.appName || 'Suivi Routes')}</div>
          <h1>Mesurez les trajets sans compliquer la journée des chauffeurs.</h1>
          <p>Une application mobile simple pour suivre les routes, les arrivées, les départs, les retards et le temps passé chez chaque client.</p>
          <div class="login-features">
            <div class="login-feature"><strong>GPS ponctuel</strong><span>Seulement lors des actions du chauffeur.</span></div>
            <div class="login-feature"><strong>Données centralisées</strong><span>Les administrateurs voient toutes les routes.</span></div>
            <div class="login-feature"><strong>Export facile</strong><span>Fichier CSV compatible avec Excel.</span></div>
          </div>
        </div>
        <small>Entrepôt principal : ${escapeHtml(config.warehouseAddress)}</small>
      </section>
      <section class="login-panel">
        <div class="login-card">
          <h2>Connexion</h2>
          <p>Utilisez le compte qui vous a été attribué.</p>
          <form id="login-form" class="login-form">
            <div class="form-field">
              <label class="required" for="login-email">Courriel</label>
              <input class="input" id="login-email" name="email" type="email" autocomplete="username" required placeholder="nom@entreprise.ca" />
            </div>
            <div class="form-field">
              <label class="required" for="login-password">Mot de passe</label>
              <input class="input" id="login-password" name="password" type="password" autocomplete="current-password" required minlength="8" placeholder="Votre mot de passe" />
            </div>
            <button class="btn btn-primary btn-lg btn-block" type="submit">Se connecter</button>
          </form>
          ${service.isDemo ? `
            <div class="demo-panel">
              <div class="demo-panel-title"><strong>Accès de démonstration</strong><span class="badge demo">Sans configuration</span></div>
              <div class="demo-buttons">
                <button class="demo-button" data-action="demo-login" data-profile="admin-1"><span><strong>Administrateur</strong><span>Voir toutes les routes et les rapports</span></span><b>→</b></button>
                <button class="demo-button" data-action="demo-login" data-profile="driver-1"><span><strong>Chauffeur — Jean Tremblay</strong><span>Tester une route de cinq arrêts</span></span><b>→</b></button>
              </div>
              <p class="help">Comptes manuels : admin@demo.local ou jean@demo.local — mot de passe demo1234.</p>
            </div>` : ''}
        </div>
      </section>
    </main>`;
}

function render() {
  setDocumentTitle();
  if (state.loading) {
    app.innerHTML = loadingScreen();
    return;
  }
  if (!state.profile) {
    app.innerHTML = loginView();
    return;
  }
  if (state.profile.role === 'admin') {
    renderAdmin();
  } else {
    renderDriver();
  }
}

async function bootstrap() {
  state.loading = true;
  render();
  try {
    const { session, profile } = await service.getCurrentUser();
    state.session = session;
    state.profile = profile;
    if (profile?.role === 'admin') await loadAdminData();
    if (profile?.role === 'driver') await loadDriverData();
  } catch (error) {
    console.error(error);
    try { await service.signOut(); } catch (_) { /* no-op */ }
    state.session = null;
    state.profile = null;
    showToast('Connexion impossible', error.message || 'Veuillez vous reconnecter.', 'error');
  } finally {
    state.loading = false;
    render();
  }
}

async function loadAdminData() {
  const [routes, drivers, subcontractors] = await Promise.all([
    service.getRoutes(state.selectedDate),
    service.listDrivers(),
    service.listSubcontractors()
  ]);
  state.routes = routes;
  state.drivers = drivers;
  state.subcontractors = subcontractors;
  if (state.adminView === 'reports') {
    state.reportRoutes = await service.getReport(state.reportFrom, state.reportTo);
  }
}

async function loadDriverData() {
  const [route, transfers] = await Promise.all([
    service.getMyRoute(todayISO()),
    service.getIncomingTransfers()
  ]);
  state.driverRoute = route;
  state.transfers = transfers;
}

function adminStats() {
  const allStops = state.routes.flatMap((route) => route.stops || []);
  const completed = allStops.filter((stop) => stop.status === 'completed').length;
  const inProgress = state.routes.filter((route) => route.status === 'in_progress').length;
  const travelTimes = [];
  let late = 0;

  state.routes.forEach((route) => {
    const stops = [...(route.stops || [])].sort((a, b) => a.planned_order - b.planned_order);
    stops.forEach((stop, index) => {
      const travel = getTravelMinutes(route, stop, stops[index - 1]);
      if (travel !== null) travelTimes.push(travel);
      if ((getDelayMinutes(route.route_date, stop) || 0) > 0) late += 1;
    });
  });

  const averageTravel = travelTimes.length
    ? Math.round(travelTimes.reduce((sum, value) => sum + value, 0) / travelTimes.length)
    : null;

  return {
    routeCount: state.routes.length,
    inProgress,
    completed,
    totalStops: allStops.length,
    averageTravel,
    late
  };
}

function routeCard(route) {
  const stops = route.stops || [];
  const completed = stops.filter((stop) => stop.status === 'completed').length;
  const percent = stops.length ? Math.round((completed / stops.length) * 100) : 0;
  const current = stops.find((stop) => stop.status === 'arrived') || stops.find((stop) => stop.status === 'planned');
  return `
    <article class="card route-card">
      <div class="route-card-top">
        <div>
          <h3 class="route-title">${escapeHtml(route.name)}</h3>
          <div class="route-driver">${escapeHtml(route.driver?.full_name || 'Chauffeur non attribué')} · ${escapeHtml(route.driver?.subcontractor?.name || 'Sans sous-traitant')}</div>
        </div>
        ${statusBadge(route.status)}
      </div>
      <div class="route-meta">
        <span>${icons.pin} ${stops.length} arrêt${stops.length === 1 ? '' : 's'}</span>
        <span>${icons.clock} Départ ${formatTime(route.started_at)}</span>
        <span>${icons.route} ${current ? escapeHtml(current.client_name) : 'Aucun prochain arrêt'}</span>
      </div>
      <div class="progress-track"><div class="progress-fill" style="width:${percent}%"></div></div>
      <div class="route-card-bottom">
        <span class="muted">${completed}/${stops.length} terminés</span>
        <button class="btn btn-light btn-sm" data-action="view-route" data-route-id="${route.id}">Ouvrir →</button>
      </div>
    </article>`;
}

function overviewView() {
  const stats = adminStats();
  return `
    <div class="page-header">
      <div>
        <h1>Tableau de bord</h1>
        <p>${escapeHtml(formatDate(state.selectedDate))}</p>
      </div>
      <div class="page-actions">
        <input class="input" type="date" value="${state.selectedDate}" data-action="change-admin-date" aria-label="Date du tableau de bord" />
        <button class="btn btn-light" data-action="refresh-admin">${icons.refresh} Actualiser</button>
        <button class="btn btn-primary" data-action="new-route">${icons.plus} Nouvelle route</button>
      </div>
    </div>
    <section class="stats-grid">
      <article class="card stat-card"><div class="stat-label">Routes</div><div class="stat-value">${stats.routeCount}</div><div class="stat-foot">${stats.inProgress} en cours</div></article>
      <article class="card stat-card"><div class="stat-label">Arrêts terminés</div><div class="stat-value">${stats.completed}/${stats.totalStops}</div><div class="stat-foot">Progression de la journée</div></article>
      <article class="card stat-card"><div class="stat-label">Trajet moyen</div><div class="stat-value">${stats.averageTravel === null ? '—' : `${stats.averageTravel} min`}</div><div class="stat-foot">Entre deux actions enregistrées</div></article>
      <article class="card stat-card"><div class="stat-label">Arrivées en retard</div><div class="stat-value">${stats.late}</div><div class="stat-foot">Selon les heures prévues</div></article>
    </section>
    ${state.routes.length
      ? `<section class="routes-grid">${state.routes.map(routeCard).join('')}</section>`
      : `<section class="card empty-state"><div class="empty-icon">${icons.route}</div><h3>Aucune route pour cette date</h3><p>Créez une route, attribuez un chauffeur et ajoutez les arrêts dans l’ordre prévu.</p><button class="btn btn-primary" data-action="new-route">Créer une route</button></section>`}
  `;
}

function routesView() {
  return `
    <div class="page-header">
      <div><h1>Routes</h1><p>Créez, copiez et modifiez les routes quotidiennes.</p></div>
      <div class="page-actions">
        <input class="input" type="date" value="${state.selectedDate}" data-action="change-admin-date" aria-label="Date des routes" />
        <button class="btn btn-primary" data-action="new-route">${icons.plus} Nouvelle route</button>
      </div>
    </div>
    <section class="card">
      <div class="card-header"><h2>Routes du ${escapeHtml(formatShortDate(state.selectedDate))}</h2><button class="btn btn-light btn-sm" data-action="refresh-admin">${icons.refresh} Actualiser</button></div>
      ${state.routes.length ? `
        <div class="table-wrap" style="border:0;border-radius:0 0 18px 18px">
          <table>
            <thead><tr><th>Route</th><th>Chauffeur</th><th>Sous-traitant</th><th>État</th><th>Arrêts</th><th>Départ</th><th>Fin</th><th></th></tr></thead>
            <tbody>
              ${state.routes.map((route) => {
                const stops = route.stops || [];
                const complete = stops.filter((stop) => stop.status === 'completed').length;
                return `<tr>
                  <td><strong>${escapeHtml(route.name)}</strong></td>
                  <td>${escapeHtml(route.driver?.full_name || '—')}</td>
                  <td>${escapeHtml(route.driver?.subcontractor?.name || '—')}</td>
                  <td>${statusBadge(route.status)}</td>
                  <td>${complete}/${stops.length}</td>
                  <td class="nowrap">${formatTime(route.started_at)}</td>
                  <td class="nowrap">${formatTime(route.completed_at)}</td>
                  <td><div class="row-actions"><button class="btn btn-light btn-sm" data-action="view-route" data-route-id="${route.id}">Ouvrir</button>${route.status === 'planned' ? `<button class="btn btn-ghost btn-sm" data-action="edit-route" data-route-id="${route.id}">${icons.edit}</button>` : ''}</div></td>
                </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>` : `<div class="empty-state"><div class="empty-icon">${icons.route}</div><h3>Aucune route</h3><p>Aucune route n’est planifiée pour cette date.</p></div>`}
    </section>`;
}

function driversView() {
  const drivers = state.drivers.filter((profile) => profile.role === 'driver');
  const admins = state.drivers.filter((profile) => profile.role === 'admin');
  const accountRows = (profiles) => profiles.map((profile) => {
    const isCurrent = profile.id === state.profile.id;
    return `<tr>
      <td><strong>${escapeHtml(profile.full_name)}</strong>${isCurrent ? ' <span class="badge demo">Vous</span>' : ''}</td>
      <td>${profile.role === 'admin' ? 'Administrateur' : 'Chauffeur'}</td>
      <td>${profile.role === 'driver' ? escapeHtml(profile.subcontractor?.name || '—') : '—'}</td>
      <td><span class="badge ${profile.active ? 'completed' : 'cancelled'}">${profile.active ? 'Actif' : 'Désactivé'}</span></td>
      <td><div class="row-actions">
        ${isCurrent
          ? '<button class="btn btn-light btn-sm" disabled>Compte actuel</button>'
          : `<button class="btn ${profile.active ? 'btn-warning' : 'btn-secondary'} btn-sm" data-action="toggle-driver" data-driver-id="${profile.id}" data-active="${profile.active ? 'false' : 'true'}">${profile.active ? 'Désactiver' : 'Réactiver'}</button>`}
        <button class="btn btn-light btn-sm" data-action="reset-password" data-driver-id="${profile.id}" data-driver-name="${escapeHtml(profile.full_name)}">Mot de passe</button>
      </div></td>
    </tr>`;
  }).join('');

  return `
    <div class="page-header">
      <div><h1>Comptes utilisateurs</h1><p>Gérez les chauffeurs et les administrateurs. Les comptes désactivés conservent leur historique.</p></div>
      <div class="page-actions">
        <button class="btn btn-light" data-action="new-subcontractor">${icons.plus} Sous-traitant</button>
        <button class="btn btn-primary" data-action="new-driver">${icons.plus} Nouveau compte</button>
      </div>
    </div>
    <section class="card">
      <div class="card-header"><h2>${drivers.length} chauffeur${drivers.length === 1 ? '' : 's'}</h2><span class="muted">Seuls les chauffeurs actifs peuvent recevoir une route.</span></div>
      ${drivers.length ? `<div class="table-wrap" style="border:0;border-radius:0 0 18px 18px"><table>
        <thead><tr><th>Nom</th><th>Rôle</th><th>Sous-traitant</th><th>État</th><th>Actions</th></tr></thead>
        <tbody>${accountRows(drivers)}</tbody>
      </table></div>` : `<div class="empty-state"><div class="empty-icon">${icons.drivers}</div><h3>Aucun chauffeur</h3><p>Créez le premier compte chauffeur.</p></div>`}
    </section>
    <section class="card" style="margin-top:16px">
      <div class="card-header"><h2>${admins.length} administrateur${admins.length === 1 ? '' : 's'}</h2><span class="muted">Chaque administrateur doit utiliser son propre compte.</span></div>
      ${admins.length ? `<div class="table-wrap" style="border:0;border-radius:0 0 18px 18px"><table>
        <thead><tr><th>Nom</th><th>Rôle</th><th>Sous-traitant</th><th>État</th><th>Actions</th></tr></thead>
        <tbody>${accountRows(admins)}</tbody>
      </table></div>` : `<div class="empty-state"><h3>Aucun administrateur visible</h3></div>`}
    </section>
    <section class="card card-pad" style="margin-top:16px">
      <h3 style="margin-top:0">Sous-traitants</h3>
      <div class="kpi-strip">${state.subcontractors.length ? state.subcontractors.map((item) => `<span class="kpi-pill"><strong>${escapeHtml(item.name)}</strong></span>`).join('') : '<span class="muted">Aucun sous-traitant.</span>'}</div>
    </section>`;
}
function reportRows(routes = state.reportRoutes) {
  const rows = [];
  routes.forEach((route) => {
    const stops = [...(route.stops || [])].sort((a, b) => a.planned_order - b.planned_order);
    stops.forEach((stop, index) => {
      const previous = stops[index - 1];
      rows.push({
        date: route.route_date,
        route: route.name,
        driver: route.driver?.full_name || '',
        subcontractor: route.driver?.subcontractor?.name || '',
        order: stop.planned_order,
        client: stop.client_name,
        address: stop.address,
        planned: formatTimeInput(stop.planned_arrival),
        arrival: formatTime(stop.arrived_at),
        departure: formatTime(stop.departed_at),
        travel: getTravelMinutes(route, stop, previous),
        service: getServiceMinutes(stop),
        delay: getDelayMinutes(route.route_date, stop),
        status: stopStatusLabel(stop.status),
        outcome: outcomeLabel(stop.outcome),
        note: stop.driver_note || '',
        unplanned: stop.is_unplanned ? 'Oui' : 'Non',
        arriveLat: stop.arrive_lat ?? '',
        arriveLng: stop.arrive_lng ?? '',
        arriveAccuracy: stop.arrive_accuracy ?? '',
        departLat: stop.depart_lat ?? '',
        departLng: stop.depart_lng ?? '',
        departAccuracy: stop.depart_accuracy ?? ''
      });
    });
  });
  return rows;
}

function reportsView() {
  const rows = reportRows();
  const completed = rows.filter((row) => row.status === 'Terminé').length;
  const travelValues = rows.map((row) => row.travel).filter((value) => value !== null);
  const avg = travelValues.length ? Math.round(travelValues.reduce((a, b) => a + b, 0) / travelValues.length) : null;
  return `
    <div class="page-header">
      <div><h1>Rapports</h1><p>Consultez les données de tous les chauffeurs et exportez-les.</p></div>
      <div class="page-actions">
        <button class="btn btn-light" data-action="export-csv" ${rows.length ? '' : 'disabled'}>${icons.report} CSV</button>
        <button class="btn btn-primary" data-action="export-xlsx" ${rows.length ? '' : 'disabled'}>${icons.report} Excel</button>
      </div>
    </div>
    <section class="card card-pad" style="margin-bottom:16px">
      <div class="filter-bar">
        <div class="form-field"><label for="report-from">Du</label><input id="report-from" class="input" type="date" value="${state.reportFrom}" /></div>
        <div class="form-field"><label for="report-to">Au</label><input id="report-to" class="input" type="date" value="${state.reportTo}" /></div>
        <button class="btn btn-primary" data-action="load-report">Afficher</button>
      </div>
      <div class="kpi-strip">
        <span class="kpi-pill"><strong>${rows.length}</strong> arrêts</span>
        <span class="kpi-pill"><strong>${completed}</strong> terminés</span>
        <span class="kpi-pill"><strong>${avg === null ? '—' : `${avg} min`}</strong> de trajet moyen</span>
      </div>
    </section>
    <section class="card">
      <div class="card-header"><h2>Détail des arrêts</h2><span class="muted">${escapeHtml(formatShortDate(state.reportFrom))} au ${escapeHtml(formatShortDate(state.reportTo))}</span></div>
      ${rows.length ? `<div class="table-wrap" style="border:0;border-radius:0 0 18px 18px"><table>
        <thead><tr><th>Date</th><th>Chauffeur</th><th>Route</th><th>Client</th><th>Prévu</th><th>Arrivée</th><th>Départ</th><th>Trajet</th><th>Chez client</th><th>Retard</th><th>Résultat</th></tr></thead>
        <tbody>${rows.map((row) => `<tr>
          <td class="nowrap">${escapeHtml(formatShortDate(row.date))}</td>
          <td>${escapeHtml(row.driver)}</td>
          <td>${escapeHtml(row.route)}</td>
          <td><strong>${escapeHtml(row.client)}</strong><br><span class="muted">${escapeHtml(row.address)}</span></td>
          <td>${escapeHtml(row.planned || '—')}</td>
          <td>${escapeHtml(row.arrival)}</td>
          <td>${escapeHtml(row.departure)}</td>
          <td>${formatDuration(row.travel)}</td>
          <td>${formatDuration(row.service)}</td>
          <td class="${row.delay ? 'text-danger' : ''}">${row.delay === null ? '—' : row.delay ? `${row.delay} min` : 'À l’heure'}</td>
          <td>${escapeHtml(row.outcome)}</td>
        </tr>`).join('')}</tbody>
      </table></div>` : `<div class="empty-state"><div class="empty-icon">${icons.report}</div><h3>Aucune donnée</h3><p>Aucun arrêt n’a été trouvé pour cette période.</p></div>`}
    </section>`;
}

function renderAdmin() {
  let content = overviewView();
  if (state.adminView === 'routes') content = routesView();
  if (state.adminView === 'drivers') content = driversView();
  if (state.adminView === 'reports') content = reportsView();
  app.innerHTML = `<div class="app-shell">${baseTopbar(`${state.profile.full_name} · Administrateur`)}${adminTabs()}<main class="page">${content}</main></div>`;
}

function routeStopEditorRow(stop = {}, index = 0) {
  const locked = stop.status && stop.status !== 'planned';
  return `
    <div class="stop-editor-row" data-stop-row data-stop-id="${escapeHtml(stop.id || '')}">
      <div class="stop-editor-order" data-stop-order>${index + 1}</div>
      <input class="input" name="client_name" value="${escapeHtml(stop.client_name || '')}" placeholder="Nom du client" required ${locked ? 'readonly' : ''} />
      <input class="input" name="address" value="${escapeHtml(stop.address || '')}" placeholder="Adresse complète" required ${locked ? 'readonly' : ''} />
      <input class="input" name="planned_arrival" type="time" value="${escapeHtml(formatTimeInput(stop.planned_arrival))}" aria-label="Heure prévue" ${locked ? 'readonly' : ''} />
      <div class="stop-editor-actions">
        ${locked ? `<span class="badge ${escapeHtml(stop.status)}">${escapeHtml(stopStatusLabel(stop.status))}</span>` : `
          <button class="btn btn-light btn-sm icon-btn" type="button" data-action="move-stop-up" title="Monter">↑</button>
          <button class="btn btn-light btn-sm icon-btn" type="button" data-action="move-stop-down" title="Descendre">↓</button>
          <button class="btn btn-ghost btn-sm icon-btn text-danger" type="button" data-action="remove-stop-row" title="Retirer">${icons.trash}</button>`}
      </div>
    </div>`;
}

function updateStopEditorOrders() {
  modalRoot.querySelectorAll('[data-stop-row]').forEach((row, index) => {
    const order = row.querySelector('[data-stop-order]');
    if (order) order.textContent = String(index + 1);
  });
}

function openRouteEditor(route = null) {
  if (!state.drivers.some((driver) => driver.role === 'driver' && driver.active)) {
    showToast('Aucun chauffeur actif', 'Créez ou réactivez un chauffeur avant de créer une route.', 'warning');
    return;
  }
  const stops = route?.stops?.length
    ? [...route.stops].sort((a, b) => a.planned_order - b.planned_order)
    : Array.from({ length: 4 }, () => ({}));
  openModal(`
    <form id="route-form" data-route-id="${escapeHtml(route?.id || '')}">
      <div class="modal-header">
        <div><h2>${route ? 'Modifier la route' : 'Nouvelle route'}</h2><p>Les routes habituelles comptent entre 4 et 12 arrêts.</p></div>
        <button class="btn btn-ghost icon-btn" type="button" data-action="close-modal" aria-label="Fermer">×</button>
      </div>
      <div class="modal-body">
        <div class="form-grid-3">
          <div class="form-field"><label class="required">Date</label><input class="input" name="route_date" type="date" value="${escapeHtml(route?.route_date || state.selectedDate)}" required /></div>
          <div class="form-field"><label class="required">Nom de la route</label><input class="input" name="name" value="${escapeHtml(route?.name || '')}" placeholder="Ex. Route 101" required /></div>
          <div class="form-field"><label class="required">Chauffeur</label>
            ${route && route.status !== 'planned' ? `<input type="hidden" name="driver_id" value="${route.driver_id}" />` : ''}
            <select class="select" name="${route && route.status !== 'planned' ? 'driver_id_display' : 'driver_id'}" required ${route && route.status !== 'planned' ? 'disabled' : ''}>
              <option value="">Choisir…</option>
              ${state.drivers.filter((driver) => driver.role === 'driver' && (driver.active || driver.id === route?.driver_id)).map((driver) => `<option value="${driver.id}" ${driver.id === route?.driver_id ? 'selected' : ''}>${escapeHtml(driver.full_name)}${driver.subcontractor?.name ? ` — ${escapeHtml(driver.subcontractor.name)}` : ''}</option>`).join('')}
            </select>
            ${route && route.status !== 'planned' ? '<div class="help">Le chauffeur d’une route commencée ne peut plus être changé ici. Réattribuez plutôt les arrêts non commencés.</div>' : ''}
          </div>
          <div class="form-field full"><label>Adresse de départ</label><input class="input" name="warehouse_address" value="${escapeHtml(route?.warehouse_address || config.warehouseAddress)}" /></div>
        </div>
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:22px">
          <div><strong>Arrêts</strong><div class="help">L’ordre affiché sera l’ordre de la route.</div></div>
          <button class="btn btn-secondary btn-sm" type="button" data-action="add-stop-row">${icons.plus} Ajouter un arrêt</button>
        </div>
        <div class="stop-editor-list" id="stop-editor-list">${stops.map(routeStopEditorRow).join('')}</div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-light" type="button" data-action="close-modal">Annuler</button>
        <button class="btn btn-primary" type="submit">Enregistrer la route</button>
      </div>
    </form>`, 'modal-lg');
}

function routeDetailRows(route) {
  const stops = [...(route.stops || [])].sort((a, b) => a.planned_order - b.planned_order);
  return stops.map((stop, index) => {
    const travel = getTravelMinutes(route, stop, stops[index - 1]);
    const serviceMinutes = getServiceMinutes(stop);
    const delay = getDelayMinutes(route.route_date, stop);
    return `<tr>
      <td><strong>${stop.planned_order}</strong></td>
      <td><strong>${escapeHtml(stop.client_name)}</strong>${stop.is_unplanned ? ' <span class="badge unplanned">Imprévu</span>' : ''}<br><span class="muted">${escapeHtml(stop.address)}</span></td>
      <td>${escapeHtml(formatTimeInput(stop.planned_arrival) || '—')}</td>
      <td>${statusBadge(stop.status, 'stop')}</td>
      <td class="nowrap">${formatTime(stop.arrived_at)}</td>
      <td class="nowrap">${formatTime(stop.departed_at)}</td>
      <td>${formatDuration(travel)}</td>
      <td>${formatDuration(serviceMinutes)}</td>
      <td class="${delay ? 'text-danger' : ''}">${delay === null ? '—' : delay ? `${delay} min` : 'À l’heure'}</td>
      <td>${escapeHtml(outcomeLabel(stop.outcome))}${stop.driver_note ? `<br><span class="muted">${escapeHtml(stop.driver_note)}</span>` : ''}</td>
      <td>${stop.status === 'planned' ? `<button class="btn btn-light btn-sm" data-action="reassign-stop" data-stop-id="${stop.id}" data-route-id="${route.id}">${icons.transfer} Réattribuer</button>` : ''}</td>
    </tr>`;
  }).join('');
}

function openRouteDetail(routeId) {
  const route = state.routes.find((item) => item.id === routeId);
  if (!route) return;
  const stops = route.stops || [];
  const completed = stops.filter((stop) => stop.status === 'completed').length;
  const routeMinutes = minutesBetween(route.started_at, route.completed_at);
  openModal(`
    <div class="modal-header">
      <div><h2>${escapeHtml(route.name)}</h2><p>${escapeHtml(route.driver?.full_name || '—')} · ${escapeHtml(formatDate(route.route_date, { weekday: undefined }))}</p></div>
      <button class="btn btn-ghost icon-btn" data-action="close-modal" aria-label="Fermer">×</button>
    </div>
    <div class="modal-body">
      <div class="detail-grid">
        <div class="detail-box"><span>État</span><strong>${routeStatusLabel(route.status)}</strong></div>
        <div class="detail-box"><span>Progression</span><strong>${completed}/${stops.length} arrêts</strong></div>
        <div class="detail-box"><span>Départ</span><strong>${formatTime(route.started_at)}</strong></div>
        <div class="detail-box"><span>Durée totale</span><strong>${formatDuration(routeMinutes)}</strong></div>
      </div>
      <div class="row-actions" style="margin-bottom:16px">
        ${route.status === 'planned' ? `<button class="btn btn-secondary btn-sm" data-action="edit-route" data-route-id="${route.id}">${icons.edit} Modifier</button>` : ''}
        <button class="btn btn-light btn-sm" data-action="duplicate-route" data-route-id="${route.id}">${icons.copy} Copier</button>
        ${route.status !== 'completed' ? `<button class="btn btn-light btn-sm" data-action="admin-add-stop" data-route-id="${route.id}">${icons.plus} Ajouter un arrêt</button>` : ''}
        ${route.status === 'planned' ? `<button class="btn btn-ghost btn-sm text-danger" data-action="delete-route" data-route-id="${route.id}">${icons.trash} Supprimer</button>` : ''}
      </div>
      ${stops.length ? `<div class="table-wrap"><table>
        <thead><tr><th>#</th><th>Client</th><th>Prévu</th><th>État</th><th>Arrivée</th><th>Départ</th><th>Trajet</th><th>Chez client</th><th>Retard</th><th>Résultat</th><th></th></tr></thead>
        <tbody>${routeDetailRows(route)}</tbody>
      </table></div>` : `<div class="empty-state"><h3>Aucun arrêt</h3><p>Ajoutez le premier arrêt à cette route.</p></div>`}
    </div>
    <div class="modal-footer"><button class="btn btn-light" data-action="close-modal">Fermer</button></div>`, 'modal-lg');
}

function openDuplicateRoute(routeId) {
  const route = state.routes.find((item) => item.id === routeId);
  if (!route) return;
  const tomorrow = new Date(`${route.route_date}T12:00:00`);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const local = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  openModal(`
    <form id="duplicate-route-form" data-route-id="${route.id}">
      <div class="modal-header"><div><h2>Copier ${escapeHtml(route.name)}</h2><p>Les arrêts et les heures prévues seront recopiés.</p></div><button class="btn btn-ghost icon-btn" type="button" data-action="close-modal">×</button></div>
      <div class="modal-body"><div class="form-grid">
        <div class="form-field"><label class="required">Nouvelle date</label><input class="input" name="target_date" type="date" value="${local}" required /></div>
        <div class="form-field"><label class="required">Chauffeur</label><select class="select" name="driver_id" required>${state.drivers.filter((driver) => driver.role === 'driver' && driver.active).map((driver) => `<option value="${driver.id}" ${driver.id === route.driver_id ? 'selected' : ''}>${escapeHtml(driver.full_name)}</option>`).join('')}</select></div>
      </div></div>
      <div class="modal-footer"><button class="btn btn-light" type="button" data-action="close-modal">Annuler</button><button class="btn btn-primary" type="submit">Créer la copie</button></div>
    </form>`);
}

function openReassignStop(stopId, routeId) {
  const route = state.routes.find((item) => item.id === routeId);
  const stop = route?.stops?.find((item) => item.id === stopId);
  if (!stop) return;
  openModal(`
    <form id="reassign-stop-form" data-stop-id="${stop.id}" data-source-route-id="${route.id}">
      <div class="modal-header"><div><h2>Réattribuer l’arrêt</h2><p>${escapeHtml(stop.client_name)}</p></div><button class="btn btn-ghost icon-btn" type="button" data-action="close-modal">×</button></div>
      <div class="modal-body">
        <div class="alert alert-info" style="margin-bottom:15px">L’arrêt sera ajouté à la fin de la route du chauffeur choisi. L’historique du chauffeur initial sera conservé.</div>
        <div class="form-field"><label class="required">Nouveau chauffeur</label><select class="select" name="driver_id" required><option value="">Choisir…</option>${state.drivers.filter((driver) => driver.role === 'driver' && driver.active && driver.id !== stop.assigned_driver_id).map((driver) => `<option value="${driver.id}">${escapeHtml(driver.full_name)}${driver.subcontractor?.name ? ` — ${escapeHtml(driver.subcontractor.name)}` : ''}</option>`).join('')}</select></div>
      </div>
      <div class="modal-footer"><button class="btn btn-light" type="button" data-action="close-modal">Annuler</button><button class="btn btn-primary" type="submit">Réattribuer</button></div>
    </form>`);
}

function openAdminAddStop(routeId) {
  const route = state.routes.find((item) => item.id === routeId);
  if (!route) return;
  openModal(`
    <form id="admin-add-stop-form" data-route-id="${route.id}">
      <div class="modal-header"><div><h2>Ajouter un arrêt</h2><p>${escapeHtml(route.name)} · ${escapeHtml(route.driver?.full_name || '')}</p></div><button class="btn btn-ghost icon-btn" type="button" data-action="close-modal">×</button></div>
      <div class="modal-body"><div class="form-grid">
        <div class="form-field"><label class="required">Client</label><input class="input" name="client_name" required /></div>
        <div class="form-field"><label class="required">Adresse</label><input class="input" name="address" required /></div>
        <div class="form-field full"><label>Raison ou note</label><textarea class="textarea" name="reason" placeholder="Ex. Ajout demandé par la répartition"></textarea></div>
      </div></div>
      <div class="modal-footer"><button class="btn btn-light" type="button" data-action="close-modal">Annuler</button><button class="btn btn-primary" type="submit">Ajouter</button></div>
    </form>`);
}

function openNewDriver() {
  openModal(`
    <form id="driver-form">
      <div class="modal-header"><div><h2>Nouveau compte</h2><p>Le chauffeur pourra se connecter avec son courriel et ce mot de passe.</p></div><button class="btn btn-ghost icon-btn" type="button" data-action="close-modal">×</button></div>
      <div class="modal-body"><div class="form-grid">
        <div class="form-field"><label class="required">Nom complet</label><input class="input" name="full_name" required /></div>
        <div class="form-field"><label class="required">Courriel</label><input class="input" name="email" type="email" required /></div>
        <div class="form-field"><label class="required">Rôle</label><select class="select" name="role"><option value="driver">Chauffeur</option><option value="admin">Administrateur</option></select></div>
        <div class="form-field"><label>Sous-traitant</label><select class="select" name="subcontractor_id"><option value="">Aucun</option>${state.subcontractors.filter((item) => item.active !== false).map((item) => `<option value="${item.id}">${escapeHtml(item.name)}</option>`).join('')}</select></div>
        <div class="form-field full"><label class="required">Mot de passe temporaire</label><input class="input" name="password" type="text" minlength="8" value="Route-${new Date().getFullYear()}!" required /><div class="help">Communiquez ce mot de passe au chauffeur de façon sécuritaire.</div></div>
      </div></div>
      <div class="modal-footer"><button class="btn btn-light" type="button" data-action="close-modal">Annuler</button><button class="btn btn-primary" type="submit">Créer le compte</button></div>
    </form>`);
}

function openNewSubcontractor() {
  openModal(`
    <form id="subcontractor-form">
      <div class="modal-header"><div><h2>Nouveau sous-traitant</h2></div><button class="btn btn-ghost icon-btn" type="button" data-action="close-modal">×</button></div>
      <div class="modal-body"><div class="form-field"><label class="required">Nom</label><input class="input" name="name" required /></div></div>
      <div class="modal-footer"><button class="btn btn-light" type="button" data-action="close-modal">Annuler</button><button class="btn btn-primary" type="submit">Ajouter</button></div>
    </form>`);
}

function openResetPassword(driverId, driverName) {
  openModal(`
    <form id="reset-password-form" data-driver-id="${driverId}">
      <div class="modal-header"><div><h2>Nouveau mot de passe</h2><p>${escapeHtml(driverName)}</p></div><button class="btn btn-ghost icon-btn" type="button" data-action="close-modal">×</button></div>
      <div class="modal-body"><div class="form-field"><label class="required">Mot de passe</label><input class="input" name="password" type="text" minlength="8" required value="Route-${new Date().getFullYear()}!" /></div></div>
      <div class="modal-footer"><button class="btn btn-light" type="button" data-action="close-modal">Annuler</button><button class="btn btn-primary" type="submit">Mettre à jour</button></div>
    </form>`);
}

function incomingTransfersView() {
  if (!state.transfers.length) return '';
  return `
    <section style="margin-bottom:16px">
      ${state.transfers.map((transfer) => `<article class="transfer-card">
        <h3>Nouvel arrêt proposé par ${escapeHtml(transfer.from_driver?.full_name || 'un chauffeur')}</h3>
        <p><strong>${escapeHtml(transfer.stop?.client_name || '')}</strong><br>${escapeHtml(transfer.stop?.address || '')}</p>
        <div class="transfer-actions">
          <button class="btn btn-primary btn-sm" data-action="respond-transfer" data-transfer-id="${transfer.id}" data-accept="true">Accepter</button>
          <button class="btn btn-light btn-sm" data-action="respond-transfer" data-transfer-id="${transfer.id}" data-accept="false">Refuser</button>
        </div>
      </article>`).join('')}
    </section>`;
}

function driverActionCard(route) {
  if (route.status === 'planned') {
    return `
      <section class="card action-card">
        <div class="action-card-head"><div class="eyebrow">Début de journée</div><h2>Prêt à partir?</h2><p class="action-address">${escapeHtml(route.warehouse_address || config.warehouseAddress)}</p></div>
        <div class="action-card-body">
          <div class="gps-notice">${icons.pin}<span>Votre position sera enregistrée uniquement lorsque vous appuierez sur le bouton.</span></div>
          <button class="btn btn-primary btn-lg btn-block" data-action="start-route" data-route-id="${route.id}">Départ de l’entrepôt</button>
        </div>
      </section>`;
  }

  if (route.status === 'completed') {
    return `
      <section class="card action-card">
        <div class="action-card-body empty-state" style="padding:30px 18px"><div class="empty-icon">${icons.check}</div><h3>Route terminée</h3><p>Départ à ${formatTime(route.started_at)} · Fin à ${formatTime(route.completed_at)} · Durée ${formatDuration(minutesBetween(route.started_at, route.completed_at))}</p></div>
      </section>`;
  }

  const ordered = [...(route.stops || [])].sort((a, b) => a.planned_order - b.planned_order);
  const current = ordered.find((stop) => stop.status === 'arrived') || ordered.find((stop) => stop.status === 'planned');
  if (!current) {
    return `
      <section class="card action-card">
        <div class="action-card-head"><div class="eyebrow">Tous les arrêts sont terminés</div><h2>Fermer la route</h2><p class="action-address">Confirmez la fin de votre journée de route.</p></div>
        <div class="action-card-body"><button class="btn btn-primary btn-lg btn-block" data-action="complete-route" data-route-id="${route.id}">Terminer la route</button></div>
      </section>`;
  }

  if (current.status === 'planned') {
    return `
      <section class="card action-card">
        <div class="action-card-head"><div class="eyebrow">Prochain arrêt · #${current.planned_order}</div><h2>${escapeHtml(current.client_name)}</h2><p class="action-address">${escapeHtml(current.address)}</p></div>
        <div class="action-card-body">
          <div class="kpi-strip" style="margin:0 0 14px"><span class="kpi-pill"><strong>Prévu</strong> ${escapeHtml(formatTimeInput(current.planned_arrival) || 'Aucune heure')}</span>${current.is_unplanned ? '<span class="badge unplanned">Imprévu</span>' : ''}</div>
          <div class="driver-primary-actions">
            <a class="btn btn-light btn-lg btn-block" href="${mapLink(current.address)}" target="_blank" rel="noopener">${icons.map} Ouvrir la navigation</a>
            <button class="btn btn-primary btn-lg btn-block" data-action="arrive-stop" data-stop-id="${current.id}">Je suis arrivé</button>
            <button class="btn btn-ghost btn-sm" data-action="transfer-stop" data-stop-id="${current.id}">${icons.transfer} Transférer cet arrêt</button>
          </div>
        </div>
      </section>`;
  }

  return `
    <section class="card action-card">
      <div class="action-card-head"><div class="eyebrow">Vous êtes sur place · #${current.planned_order}</div><h2>${escapeHtml(current.client_name)}</h2><p class="action-address">Arrivée enregistrée à ${formatTime(current.arrived_at)}</p></div>
      <div class="action-card-body">
        <form id="depart-stop-form" data-stop-id="${current.id}">
          <div class="form-field" style="margin-bottom:12px"><label class="required">Résultat de l’arrêt</label></div>
          <div class="outcome-grid">
            ${[
              ['pickup', 'Ramassage effectué'],
              ['delivery', 'Livraison effectuée'],
              ['pickup_delivery', 'Ramassage et livraison'],
              ['absent', 'Client absent'],
              ['exception', 'Problème ou exception'],
              ['other', 'Autre']
            ].map(([value, label], index) => `<div class="outcome-option"><input id="outcome-${index}" type="radio" name="outcome" value="${value}" required><label for="outcome-${index}">${label}</label></div>`).join('')}
          </div>
          <div class="form-field" style="margin-bottom:14px"><label>Note facultative</label><textarea class="textarea" name="driver_note" placeholder="Ex. 3 palettes ramassées, client absent, délai à la réception…"></textarea></div>
          <button class="btn btn-primary btn-lg btn-block" type="submit">Je quitte le client</button>
        </form>
      </div>
    </section>`;
}

function driverStopsView(route) {
  const ordered = [...(route.stops || [])].sort((a, b) => a.planned_order - b.planned_order);
  return `
    <section class="card">
      <div class="card-header"><h2>Itinéraire</h2><button class="btn btn-light btn-sm" data-action="driver-add-stop" data-route-id="${route.id}" ${route.status === 'completed' ? 'disabled' : ''}>${icons.plus} Arrêt imprévu</button></div>
      <div class="card-body"><div class="stops-list">
        ${ordered.map((stop) => `<article class="card stop-item ${escapeHtml(stop.status)}">
          <div class="stop-number">${stop.status === 'completed' ? icons.check : stop.planned_order}</div>
          <div class="stop-copy">
            <h3>${escapeHtml(stop.client_name)} ${stop.is_unplanned ? '<span class="badge unplanned">Imprévu</span>' : ''}</h3>
            <p>${escapeHtml(stop.address)}</p>
            <div class="stop-meta">
              <span>Prévu ${escapeHtml(formatTimeInput(stop.planned_arrival) || '—')}</span>
              ${stop.arrived_at ? `<span>Arrivée ${formatTime(stop.arrived_at)}</span>` : ''}
              ${stop.departed_at ? `<span>Départ ${formatTime(stop.departed_at)}</span>` : ''}
              ${stop.outcome ? `<span>${escapeHtml(outcomeLabel(stop.outcome))}</span>` : ''}
            </div>
          </div>
          <div class="stop-actions">${statusBadge(stop.status, 'stop')}</div>
        </article>`).join('')}
      </div></div>
    </section>`;
}

function renderDriver() {
  const route = state.driverRoute;
  if (!route) {
    app.innerHTML = `<div class="app-shell">${baseTopbar(`${state.profile.full_name} · Chauffeur`)}<main class="page driver-layout">${incomingTransfersView()}<section class="card empty-state"><div class="empty-icon">${icons.route}</div><h3>Aucune route aujourd’hui</h3><p>Votre administrateur n’a pas encore attribué de route pour le ${escapeHtml(formatDate(todayISO()))}.</p><button class="btn btn-light" data-action="refresh-driver">${icons.refresh} Actualiser</button></section></main></div>`;
    return;
  }
  const stops = route.stops || [];
  const completed = stops.filter((stop) => stop.status === 'completed').length;
  const percent = stops.length ? Math.round((completed / stops.length) * 100) : 0;
  app.innerHTML = `
    <div class="app-shell">
      ${baseTopbar(`${state.profile.full_name} · Chauffeur`)}
      <main class="page driver-layout">
        ${incomingTransfersView()}
        <section class="card driver-hero">
          <div class="driver-hero-top"><div><h1>${escapeHtml(route.name)}</h1><p>${escapeHtml(formatDate(route.route_date))}</p></div>${statusBadge(route.status)}</div>
          <div class="driver-progress"><div class="progress-track"><div class="progress-fill" style="width:${percent}%"></div></div><div class="driver-progress-meta"><span>${completed}/${stops.length} arrêts terminés</span><span>${percent}%</span></div></div>
        </section>
        ${driverActionCard(route)}
        ${driverStopsView(route)}
      </main>
    </div>`;
}

function openDriverAddStop(routeId) {
  openModal(`
    <form id="driver-add-stop-form" data-route-id="${routeId}">
      <div class="modal-header"><div><h2>Arrêt imprévu</h2><p>Ajoutez un arrêt reçu pendant la route.</p></div><button class="btn btn-ghost icon-btn" type="button" data-action="close-modal">×</button></div>
      <div class="modal-body"><div class="form-grid">
        <div class="form-field"><label class="required">Client</label><input class="input" name="client_name" required /></div>
        <div class="form-field"><label class="required">Adresse</label><input class="input" name="address" required /></div>
        <div class="form-field full"><label class="required">Raison</label><select class="select" name="reason" required><option value="">Choisir…</option><option>Demande de l’administrateur</option><option>Ramassage ajouté par le client</option><option>Livraison ajoutée</option><option>Autre</option></select></div>
        <div class="form-field full"><label>Note</label><textarea class="textarea" name="driver_note"></textarea></div>
      </div></div>
      <div class="modal-footer"><button class="btn btn-light" type="button" data-action="close-modal">Annuler</button><button class="btn btn-primary" type="submit">Ajouter l’arrêt</button></div>
    </form>`);
}

async function openTransferStop(stopId) {
  try {
    const candidates = await service.getTransferCandidates();
    if (!candidates.length) {
      showToast('Aucun chauffeur disponible', 'Aucun autre chauffeur actif du même sous-traitant n’est disponible.', 'warning');
      return;
    }
    const stop = state.driverRoute?.stops?.find((item) => item.id === stopId);
    openModal(`
      <form id="transfer-stop-form" data-stop-id="${stopId}">
        <div class="modal-header"><div><h2>Transférer l’arrêt</h2><p>${escapeHtml(stop?.client_name || '')}</p></div><button class="btn btn-ghost icon-btn" type="button" data-action="close-modal">×</button></div>
        <div class="modal-body">
          <div class="alert alert-warning" style="margin-bottom:14px">Le chauffeur choisi devra accepter le transfert avant que l’arrêt soit déplacé.</div>
          <div class="form-field"><label class="required">Chauffeur destinataire</label><select class="select" name="to_driver_id" required><option value="">Choisir…</option>${candidates.map((driver) => `<option value="${driver.id}">${escapeHtml(driver.full_name)}</option>`).join('')}</select></div>
        </div>
        <div class="modal-footer"><button class="btn btn-light" type="button" data-action="close-modal">Annuler</button><button class="btn btn-primary" type="submit">Envoyer la demande</button></div>
      </form>`);
  } catch (error) {
    showToast('Transfert impossible', error.message, 'error');
  }
}

function setButtonBusy(button, busy, label = 'Traitement…') {
  if (!button) return;
  if (busy) {
    button.dataset.originalHtml = button.innerHTML;
    button.disabled = true;
    button.innerHTML = `<span class="loading-inline">${escapeHtml(label)}</span>`;
  } else {
    button.disabled = false;
    if (button.dataset.originalHtml) button.innerHTML = button.dataset.originalHtml;
  }
}

async function locationPayload() {
  if (!navigator.onLine && !service.isDemo) throw new Error('Une connexion Internet est requise pour enregistrer cette action.');
  try {
    const location = await geolocate();
    return { device_at: new Date().toISOString(), lat: location.lat, lng: location.lng, accuracy: location.accuracy };
  } catch (error) {
    const proceed = window.confirm(`${error.message}\n\nVoulez-vous enregistrer l’action sans position GPS?`);
    if (!proceed) throw error;
    return { device_at: new Date().toISOString(), lat: null, lng: null, accuracy: null };
  }
}

async function refreshAdmin(message = null) {
  await loadAdminData();
  render();
  if (message) showToast(message);
}

async function refreshDriver(message = null) {
  await loadDriverData();
  render();
  if (message) showToast(message);
}

function reportExportColumns() {
  return [
    ['Date', (row) => row.date],
    ['Chauffeur', (row) => row.driver],
    ['Sous-traitant', (row) => row.subcontractor],
    ['Route', (row) => row.route],
    ['Ordre', (row) => row.order],
    ['Client', (row) => row.client],
    ['Adresse', (row) => row.address],
    ['Heure prévue', (row) => row.planned],
    ['Arrivée', (row) => row.arrival],
    ['Départ', (row) => row.departure],
    ['Trajet (min)', (row) => row.travel ?? ''],
    ['Chez le client (min)', (row) => row.service ?? ''],
    ['Retard (min)', (row) => row.delay ?? ''],
    ['État', (row) => row.status],
    ['Résultat', (row) => row.outcome],
    ['Note', (row) => row.note],
    ['Imprévu', (row) => row.unplanned],
    ['Latitude arrivée', (row) => row.arriveLat],
    ['Longitude arrivée', (row) => row.arriveLng],
    ['Précision arrivée (m)', (row) => row.arriveAccuracy],
    ['Latitude départ', (row) => row.departLat],
    ['Longitude départ', (row) => row.departLng],
    ['Précision départ (m)', (row) => row.departAccuracy]
  ].map(([label, value]) => ({ label, value }));
}

function exportReportCSV() {
  const rows = reportRows();
  const columns = reportExportColumns();
  downloadText(`suivi-routes_${state.reportFrom}_${state.reportTo}.csv`, toCSV(rows, columns));
  showToast('Export créé', 'Le fichier CSV peut être ouvert directement dans Excel.');
}

async function exportReportXLSX() {
  const rows = reportRows();
  if (!rows.length) throw new Error('Aucune donnée à exporter.');
  const columns = reportExportColumns();
  const sheetData = [
    columns.map((column) => ({
      value: column.label,
      fontWeight: 'bold',
      backgroundColor: '#d9eef3',
      alignVertical: 'center'
    })),
    ...rows.map((row) => columns.map((column) => ({ value: column.value(row) ?? '' })))
  ];
  const widths = columns.map((column) => {
    if (column.label === 'Adresse' || column.label === 'Note') return { width: 34 };
    if (column.label === 'Client' || column.label === 'Chauffeur' || column.label === 'Sous-traitant') return { width: 22 };
    return { width: 16 };
  });
  const { default: writeExcelFile } = await import('write-excel-file/browser');
  await writeExcelFile(sheetData, { columns: widths, stickyRowsCount: 1 })
    .toFile(`suivi-routes_${state.reportFrom}_${state.reportTo}.xlsx`);
  showToast('Export Excel créé', 'Le fichier .xlsx a été téléchargé.');
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-action]');
  if (!button) return;
  const action = button.dataset.action;

  try {
    if (action === 'logout') {
      setButtonBusy(button, true, 'Déconnexion');
      await service.signOut();
      state.session = null;
      state.profile = null;
      state.routes = [];
      state.driverRoute = null;
      closeModal();
      render();
      return;
    }

    if (action === 'demo-login') {
      setButtonBusy(button, true, 'Ouverture');
      await service.demoLogin(button.dataset.profile);
      await bootstrap();
      return;
    }

    if (action === 'admin-nav') {
      state.adminView = button.dataset.view;
      if (state.adminView === 'reports') state.reportRoutes = await service.getReport(state.reportFrom, state.reportTo);
      render();
      return;
    }

    if (action === 'refresh-admin') {
      setButtonBusy(button, true, 'Actualisation');
      await refreshAdmin('Données actualisées');
      return;
    }

    if (action === 'refresh-driver') {
      setButtonBusy(button, true, 'Actualisation');
      await refreshDriver('Route actualisée');
      return;
    }

    if (action === 'new-route') return openRouteEditor();
    if (action === 'view-route') return openRouteDetail(button.dataset.routeId);
    if (action === 'edit-route') {
      const route = state.routes.find((item) => item.id === button.dataset.routeId);
      closeModal();
      return openRouteEditor(route);
    }
    if (action === 'duplicate-route') return openDuplicateRoute(button.dataset.routeId);
    if (action === 'reassign-stop') return openReassignStop(button.dataset.stopId, button.dataset.routeId);
    if (action === 'admin-add-stop') return openAdminAddStop(button.dataset.routeId);
    if (action === 'new-driver') return openNewDriver();
    if (action === 'new-subcontractor') return openNewSubcontractor();
    if (action === 'reset-password') return openResetPassword(button.dataset.driverId, button.dataset.driverName);
    if (action === 'driver-add-stop') return openDriverAddStop(button.dataset.routeId);
    if (action === 'transfer-stop') return openTransferStop(button.dataset.stopId);
    if (action === 'export-csv') return exportReportCSV();
    if (action === 'export-xlsx') {
      setButtonBusy(button, true, 'Création');
      await exportReportXLSX();
      setButtonBusy(button, false);
      return;
    }

    if (action === 'close-modal') return closeModal();
    if (action === 'close-modal-backdrop' && event.target === button) return closeModal();

    if (action === 'add-stop-row') {
      const list = modalRoot.querySelector('#stop-editor-list');
      const holder = document.createElement('div');
      holder.innerHTML = routeStopEditorRow({}, list.children.length);
      list.appendChild(holder.firstElementChild);
      updateStopEditorOrders();
      list.lastElementChild.querySelector('input')?.focus();
      return;
    }

    if (action === 'remove-stop-row') {
      const row = button.closest('[data-stop-row]');
      const list = row.parentElement;
      if (list.children.length <= 1) {
        showToast('Au moins un arrêt requis', '', 'warning');
        return;
      }
      row.remove();
      updateStopEditorOrders();
      return;
    }

    if (action === 'move-stop-up' || action === 'move-stop-down') {
      const row = button.closest('[data-stop-row]');
      if (action === 'move-stop-up' && row.previousElementSibling) row.parentElement.insertBefore(row, row.previousElementSibling);
      if (action === 'move-stop-down' && row.nextElementSibling) row.parentElement.insertBefore(row.nextElementSibling, row);
      updateStopEditorOrders();
      return;
    }

    if (action === 'delete-route') {
      if (!window.confirm('Supprimer cette route planifiée et tous ses arrêts?')) return;
      setButtonBusy(button, true, 'Suppression');
      await service.deleteRoute(button.dataset.routeId);
      closeModal();
      await refreshAdmin('Route supprimée');
      return;
    }

    if (action === 'toggle-driver') {
      if (button.dataset.driverId === state.profile.id) throw new Error('Vous ne pouvez pas désactiver votre propre compte.');
      const active = button.dataset.active === 'true';
      const verb = active ? 'réactiver' : 'désactiver';
      if (!window.confirm(`Voulez-vous ${verb} ce compte?`)) return;
      setButtonBusy(button, true);
      await service.setDriverActive(button.dataset.driverId, active);
      await refreshAdmin(active ? 'Compte réactivé' : 'Compte désactivé');
      return;
    }

    if (action === 'load-report') {
      const from = document.querySelector('#report-from')?.value;
      const to = document.querySelector('#report-to')?.value;
      if (!from || !to || from > to) throw new Error('Choisissez une période valide.');
      state.reportFrom = from;
      state.reportTo = to;
      setButtonBusy(button, true, 'Chargement');
      state.reportRoutes = await service.getReport(from, to);
      render();
      return;
    }

    if (action === 'start-route') {
      setButtonBusy(button, true, 'Position GPS');
      const payload = await locationPayload();
      await service.startRoute(button.dataset.routeId, payload);
      await refreshDriver('Départ de l’entrepôt enregistré');
      return;
    }

    if (action === 'arrive-stop') {
      setButtonBusy(button, true, 'Position GPS');
      const payload = await locationPayload();
      await service.arriveStop(button.dataset.stopId, payload);
      await refreshDriver('Arrivée enregistrée');
      return;
    }

    if (action === 'complete-route') {
      if (!window.confirm('Confirmer la fin de la route?')) return;
      setButtonBusy(button, true, 'Position GPS');
      const payload = await locationPayload();
      await service.completeRoute(button.dataset.routeId, payload);
      await refreshDriver('Route terminée');
      return;
    }

    if (action === 'respond-transfer') {
      const accept = button.dataset.accept === 'true';
      setButtonBusy(button, true, accept ? 'Acceptation' : 'Refus');
      await service.respondTransfer(button.dataset.transferId, accept);
      await refreshDriver(accept ? 'Arrêt ajouté à votre route' : 'Transfert refusé');
      return;
    }
  } catch (error) {
    console.error(error);
    setButtonBusy(button, false);
    showToast('Action impossible', error.message || 'Une erreur est survenue.', 'error');
  }
});

app.addEventListener('change', async (event) => {
  const input = event.target.closest('[data-action="change-admin-date"]');
  if (!input) return;
  state.selectedDate = input.value;
  try {
    await loadAdminData();
    render();
  } catch (error) {
    showToast('Chargement impossible', error.message, 'error');
  }
});

async function handleSubmit(form, submitter) {
  const data = new FormData(form);
  const id = form.id;

  if (id === 'login-form') {
    setButtonBusy(submitter, true, 'Connexion');
    await service.signIn(data.get('email').trim(), data.get('password'));
    await bootstrap();
    return;
  }

  if (id === 'route-form') {
    const stopRows = [...form.querySelectorAll('[data-stop-row]')];
    if (!stopRows.length) throw new Error('Ajoutez au moins un arrêt.');
    const stops = stopRows.map((row) => ({
      id: row.dataset.stopId || null,
      client_name: row.querySelector('[name="client_name"]').value.trim(),
      address: row.querySelector('[name="address"]').value.trim(),
      planned_arrival: row.querySelector('[name="planned_arrival"]').value || null
    }));
    if (stops.some((stop) => !stop.client_name || !stop.address)) throw new Error('Le nom et l’adresse sont requis pour chaque arrêt.');
    setButtonBusy(submitter, true, 'Enregistrement');
    await service.saveRoute({
      id: form.dataset.routeId || null,
      route_date: data.get('route_date'),
      name: data.get('name').trim(),
      driver_id: data.get('driver_id'),
      warehouse_address: data.get('warehouse_address').trim()
    }, stops);
    state.selectedDate = data.get('route_date');
    closeModal();
    await refreshAdmin('Route enregistrée');
    return;
  }

  if (id === 'duplicate-route-form') {
    setButtonBusy(submitter, true, 'Création');
    await service.duplicateRoute(form.dataset.routeId, data.get('target_date'), data.get('driver_id'));
    state.selectedDate = data.get('target_date');
    closeModal();
    await refreshAdmin('Route copiée');
    return;
  }

  if (id === 'reassign-stop-form') {
    setButtonBusy(submitter, true, 'Réattribution');
    await service.adminReassignStop(form.dataset.stopId, data.get('driver_id'));
    closeModal();
    await refreshAdmin('Arrêt réattribué');
    return;
  }

  if (id === 'admin-add-stop-form') {
    setButtonBusy(submitter, true, 'Ajout');
    await service.addUnplannedStop(form.dataset.routeId, {
      client_name: data.get('client_name').trim(),
      address: data.get('address').trim(),
      reason: data.get('reason').trim(),
      driver_note: ''
    });
    closeModal();
    await refreshAdmin('Arrêt ajouté');
    return;
  }

  if (id === 'driver-form') {
    setButtonBusy(submitter, true, 'Création');
    await service.createDriver({
      full_name: data.get('full_name').trim(),
      email: data.get('email').trim(),
      password: data.get('password'),
      role: data.get('role'),
      subcontractor_id: data.get('subcontractor_id') || null
    });
    closeModal();
    await refreshAdmin('Compte créé');
    return;
  }

  if (id === 'subcontractor-form') {
    setButtonBusy(submitter, true, 'Ajout');
    await service.createSubcontractor(data.get('name').trim());
    closeModal();
    await refreshAdmin('Sous-traitant ajouté');
    return;
  }

  if (id === 'reset-password-form') {
    setButtonBusy(submitter, true, 'Mise à jour');
    await service.resetDriverPassword(form.dataset.driverId, data.get('password'));
    closeModal();
    showToast('Mot de passe mis à jour');
    return;
  }

  if (id === 'depart-stop-form') {
    setButtonBusy(submitter, true, 'Position GPS');
    const payload = await locationPayload();
    await service.departStop(form.dataset.stopId, {
      ...payload,
      outcome: data.get('outcome'),
      driver_note: data.get('driver_note').trim()
    });
    await refreshDriver('Départ du client enregistré');
    return;
  }

  if (id === 'driver-add-stop-form') {
    setButtonBusy(submitter, true, 'Ajout');
    await service.addUnplannedStop(form.dataset.routeId, {
      client_name: data.get('client_name').trim(),
      address: data.get('address').trim(),
      reason: data.get('reason'),
      driver_note: data.get('driver_note').trim()
    });
    closeModal();
    await refreshDriver('Arrêt imprévu ajouté');
    return;
  }

  if (id === 'transfer-stop-form') {
    setButtonBusy(submitter, true, 'Envoi');
    await service.requestTransfer(form.dataset.stopId, data.get('to_driver_id'));
    closeModal();
    showToast('Demande envoyée', 'Le chauffeur destinataire doit accepter le transfert.');
  }
}

function submitListener(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  event.preventDefault();
  const submitter = event.submitter || form.querySelector('[type="submit"]');
  handleSubmit(form, submitter).catch((error) => {
    console.error(error);
    setButtonBusy(submitter, false);
    showToast('Enregistrement impossible', error.message || 'Une erreur est survenue.', 'error');
  });
}

app.addEventListener('submit', submitListener);
modalRoot.addEventListener('submit', submitListener);

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && modalRoot.innerHTML) closeModal();
});

window.addEventListener('online', () => {
  state.online = true;
  render();
  showToast('Connexion rétablie');
});

window.addEventListener('offline', () => {
  state.online = false;
  render();
  showToast('Connexion perdue', 'Les nouvelles actions nécessitent une connexion Internet.', 'warning');
});

if ('serviceWorker' in navigator && location.protocol.startsWith('http')) {
  window.addEventListener('load', () => navigator.serviceWorker.register('./service-worker.js').catch((error) => console.warn('Service worker:', error)));
}

bootstrap();
